import React, { useEffect, useMemo, useState, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkspaceComposer from "./WorkspaceComposer";
import { useChatBridge } from "./useChatBridge";
import { buildWorkspaceBridgeContext } from "./workspaceContext";
import {
  buildSettingsPath,
  findPage,
  findSection,
  getAccessibleSettingsSections,
  getDefaultSettingsTarget,
  type Tier,
  type SettingsSection,
} from "./settingsCatalog";

function useTierConfig(): { tier: Tier; loading: boolean } {
  const [tier, setTier] = useState<Tier>("starter");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/config/tier");
        if (!res.ok) return;
        const data = (await res.json()) as { tier?: string };
        if (
          !cancelled &&
          (data.tier === "starter" ||
            data.tier === "pro" ||
            data.tier === "enterprise" ||
            data.tier === "internal")
        ) {
          setTier(data.tier as Tier);
        }
      } catch {
        // Fail closed to the starter surface when tier config is unavailable.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { tier, loading };
}

function TierBadge({ tier }: { tier: Tier }) {
  const colors: Record<Tier, { bg: string; text: string }> = {
    starter: { bg: "var(--c-surface-3)", text: "var(--c-text-faint)" },
    pro: { bg: "rgba(99, 102, 241, 0.1)", text: "var(--c-primary)" },
    enterprise: { bg: "rgba(234, 179, 8, 0.1)", text: "var(--c-warning)" },
    internal: { bg: "rgba(239, 68, 68, 0.1)", text: "var(--c-danger)" },
  };
  const labels: Record<Tier, string> = {
    starter: "STARTER",
    pro: "PRO",
    enterprise: "ENTERPRISE",
    internal: "INTERNAL",
  };
  const color = colors[tier];

  return (
    <span
      className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
      style={{ backgroundColor: color.bg, color: color.text }}
    >
      {labels[tier]}
    </span>
  );
}

function PageLoading() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex items-center gap-3">
        <div
          className="w-5 h-5 border-2 rounded-full animate-spin"
          style={{ borderColor: "var(--c-border)", borderTopColor: "var(--c-primary)" }}
        />
        <span className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
          Loading...
        </span>
      </div>
    </div>
  );
}

export default function SettingsHub(): React.ReactElement {
  const navigate = useNavigate();
  const params = useParams<{ sectionId?: string; pageId?: string }>();
  const { tier, loading } = useTierConfig();
  const [navOpen, setNavOpen] = useState(false);
  const { sendChatMessage } = useChatBridge();

  const defaultTarget = getDefaultSettingsTarget();
  const activeSection = findSection(params.sectionId) ?? findSection(defaultTarget.sectionId)!;
  const activePage =
    findPage(activeSection.id, params.pageId) ??
    activeSection.pages[0] ??
    findPage(defaultTarget.sectionId, defaultTarget.pageId)!;
  const visibleSections = useMemo(() => getAccessibleSettingsSections(tier), [tier]);
  const composerContext = useMemo(() => {
    const baseContext = buildWorkspaceBridgeContext({
      surface: "settings",
      summary: { workstreams: 0, openIssues: 0 },
    });

    return {
      ...baseContext,
      settingsSectionId: activeSection.id,
      settingsSectionLabel: activeSection.label,
      settingsPageId: activePage.id,
      settingsPageLabel: activePage.label,
      workspaceSurfaceLabel: `${activeSection.label} · ${activePage.label}`,
      sidebarNodeLabel: activePage.label,
      contextSummary: `${baseContext.contextSummary} Current settings area: ${activeSection.label}. Current page: ${activePage.label}.`,
    };
  }, [activePage.id, activePage.label, activeSection.id, activeSection.label]);

  const tierGroups = useMemo(() => {
    const groups: Array<{ tier: Tier; label: string; sections: SettingsSection[] }> = [
      { tier: "starter", label: "Essentials", sections: [] },
      { tier: "pro", label: "Pro", sections: [] },
      { tier: "enterprise", label: "Enterprise", sections: [] },
      { tier: "internal", label: "Internal", sections: [] },
    ];
    for (const section of visibleSections) {
      const group = groups.find((entry) => entry.tier === section.tier);
      group?.sections.push(section);
    }
    return groups.filter((group) => group.sections.length > 0);
  }, [visibleSections]);

  useEffect(() => {
    if (!params.sectionId) return;
    const section = findSection(params.sectionId);
    if (!section) {
      navigate("/settings", { replace: true });
      return;
    }
    if (!params.pageId) return;
    if (!findPage(section.id, params.pageId)) {
      navigate(buildSettingsPath(section.id, section.pages[0]?.id), { replace: true });
    }
  }, [navigate, params.pageId, params.sectionId]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--c-bg)" }}
      >
        <PageLoading />
      </div>
    );
  }

  const handleSectionClick = (section: SettingsSection) => {
    navigate(buildSettingsPath(section.id, section.pages[0]?.id));
    setNavOpen(false);
  };

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{ backgroundColor: "var(--c-bg)" }}
    >
      <div
        className="flex items-center justify-between px-4 h-12 shrink-0"
        style={{ backgroundColor: "var(--c-surface)", borderBottom: "1px solid var(--c-border)" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => setNavOpen((open) => !open)}
            className="p-1.5 rounded-md transition-colors md:hidden"
            style={{ color: "var(--c-text-secondary)" }}
            aria-label={navOpen ? "Close settings navigation" : "Open settings navigation"}
            title={navOpen ? "Close settings navigation" : "Open settings navigation"}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = "var(--c-hover)";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = "";
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M2 4h12M2 8h12M2 12h12" />
            </svg>
          </button>
          <span className="text-sm font-semibold truncate" style={{ color: "var(--c-text)" }}>
            Settings
          </span>
          <TierBadge tier={tier} />
        </div>
        <button
          onClick={() => navigate("/")}
          className="px-3 py-1.5 rounded-md text-xs transition-colors"
          style={{ color: "var(--c-primary)" }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = "var(--c-primary-subtle)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = "";
          }}
        >
          Back to workspace
        </button>
      </div>

      <div className="flex flex-1 min-h-0 relative overflow-hidden">
        {navOpen && (
          <div
            className="absolute inset-0 z-30 md:hidden"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={() => setNavOpen(false)}
          />
        )}

        <div
          className={`${navOpen ? "absolute inset-y-0 left-0 z-40" : "hidden"} md:relative md:block w-60 shrink-0 h-full flex flex-col overflow-hidden`}
          style={{ backgroundColor: "var(--c-surface)", borderRight: "1px solid var(--c-border)" }}
        >
          <nav className="flex-1 overflow-y-auto py-2">
            {tierGroups.map((group) => {
              return (
                <div key={group.tier}>
                  <p
                    className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest flex items-center gap-2"
                    style={{ color: "var(--c-text-faint)" }}
                  >
                    {group.label}
                  </p>
                  {group.sections.map((section) => {
                    const isActive = activeSection.id === section.id;
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSectionClick(section)}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-[13px] rounded-md mx-0 transition-all duration-150"
                        style={{
                          backgroundColor: isActive ? "var(--c-primary-subtle)" : undefined,
                          color: isActive ? "var(--c-primary)" : "var(--c-text-secondary)",
                        }}
                        onMouseEnter={(event) => {
                          if (!isActive)
                            event.currentTarget.style.backgroundColor = "var(--c-hover)";
                        }}
                        onMouseLeave={(event) => {
                          if (!isActive) event.currentTarget.style.backgroundColor = "";
                        }}
                      >
                        <span className="shrink-0" style={{ opacity: 0.8 }}>
                          {section.icon}
                        </span>
                        <span className={`truncate ${isActive ? "font-medium" : ""}`}>
                          {section.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </div>

        <div
          className="flex-1 flex flex-col min-w-0 overflow-hidden"
          style={{ paddingBottom: 120 }}
        >
          <div
            className="flex items-center justify-between px-4 md:px-6 h-10 shrink-0 gap-2"
            style={{ borderBottom: "1px solid var(--c-border)" }}
          >
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
              <h2 className="text-sm font-semibold truncate" style={{ color: "var(--c-text)" }}>
                {activeSection.label}
              </h2>
              <span
                className="text-xs truncate hidden sm:inline"
                style={{ color: "var(--c-text-faint)" }}
              >
                {activeSection.description}
              </span>
            </div>

            {activeSection.pages.length > 1 && (
              <div className="flex items-center gap-1 shrink-0">
                {activeSection.pages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => navigate(buildSettingsPath(activeSection.id, page.id))}
                    className="px-2 md:px-3 py-1 text-xs rounded-md transition-colors whitespace-nowrap"
                    style={{
                      backgroundColor:
                        activePage.id === page.id ? "var(--c-primary-subtle)" : undefined,
                      color:
                        activePage.id === page.id ? "var(--c-primary)" : "var(--c-text-secondary)",
                      fontWeight: activePage.id === page.id ? 500 : 400,
                    }}
                    onMouseEnter={(event) => {
                      if (activePage.id !== page.id)
                        event.currentTarget.style.backgroundColor = "var(--c-hover)";
                    }}
                    onMouseLeave={(event) => {
                      if (activePage.id !== page.id) event.currentTarget.style.backgroundColor = "";
                    }}
                  >
                    {page.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
              <Suspense fallback={<PageLoading />}>
                <activePage.component />
              </Suspense>
            </div>
          </div>
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 z-20 flex justify-center px-2 md:px-4 md:pl-[240px]"
          style={{ bottom: 18 }}
        >
          <div className="pointer-events-auto w-full">
            <WorkspaceComposer
              surface="settings"
              context={composerContext}
              onSend={(text, context) => {
                sendChatMessage(text, context);
                navigate("/");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
